import { create } from 'zustand';
import {
  IApiResponse,
  ICountryState,
  IDeviceInfo,
  IIdentityDocumentTypes,
  IUserIpLocation,
} from '../models/resource.model';
import errorHandler from '../utils/error-handler.util';
import createAxiosInstance from '../services/global/axiosInstance';
import { storageUtil } from '../utils/storage.util';
import { IExchangeCurrency } from '../models/payout.model';
import { toTitleCaseFromUnderscore } from '../utils/string.util';
import useOnboardingStore from './onboarding';
import useRateStore from './rate';
import { countryMap } from '../utils/countries.util';

const axiosInstance = createAxiosInstance();

interface IState {
  postalCode: string;
  stateCode: string;
  stateName: string;
}

interface IResourceStore {
  loading: boolean;
  countryStates: ICountryState[];
  getAllCountryStates: (countryCode: string) => Promise<ICountryState[]>;
  getDeviceInfo: () => IDeviceInfo;
  userIpLocationData: IUserIpLocation;
  setUserIpLocationData: (data: IUserIpLocation) => void;
  getUserIpLocationData: () => Promise<any>;
  toCurrencies: IExchangeCurrency[];
  fromCurrencies: IExchangeCurrency[];
  identityDocumentTypes: IIdentityDocumentTypes[];
  getCountrySetupData: () => Promise<IApiResponse | null>;
}

const useResourceStore = create<IResourceStore>((set, get) => ({
  loading: false,
  countryStates: [],
  getAllCountryStates: async (countryCode: string) => {
    set({ loading: true });
    try {
      const response = await axiosInstance.get(
        `/country/states/${countryCode}`,
      );
      const updatedStates = response.data.value.value
        .map((state: IState) => ({
          name: state.stateName,
          code: state.stateCode,
          postalCode: state.postalCode,
        }))
        .sort((a: ICountryState, b: ICountryState) =>
          a.name.localeCompare(b.name),
        );

      set((state) => {
        const newState = { ...state, countryStates: updatedStates };
        return newState;
      });
      set({ loading: false });

      return response.data;
    } catch (err: any) {
      set({ loading: false });
      errorHandler(err);
      console.log(err);
    }
  },
  getDeviceInfo: () => {
    const userAgent = navigator.userAgent;
    let deviceType = 'Desktop';

    if (/Mobi|Android/i.test(userAgent)) {
      deviceType = 'Mobile';
    } else if (/iPad|Tablet/i.test(userAgent)) {
      deviceType = 'Tablet';
    }

    return {
      userAgent,
      deviceType,
      platform: navigator.platform,
    };
  },
  userIpLocationData: {
    ipAddress: '',
    city: '',
    state: '',
    countryName: '',
    userAgent: '',
    deviceType: '',
    platform: '',
  },
  setUserIpLocationData: (data: IUserIpLocation) => {
    set({ userIpLocationData: { ...data } });
    storageUtil.setItem('user-ip-location', data, 'local');
  },
  getUserIpLocationData: async () => {
    try {
      const response = await axiosInstance.get('https://ipapi.co/json/');
      if (response && response.data) {
        const deviceInfo: IDeviceInfo = get().getDeviceInfo();
        get().setUserIpLocationData({
          ipAddress: response.data.ip,
          city: response.data.city,
          state: response.data.region,
          countryName: response.data.country_name,
          ...deviceInfo,
        });
      }

      return response.data;
    } catch (err: any) {
      // errorHandler(err);
    }
  },
  toCurrencies: [],
  fromCurrencies: [],
  identityDocumentTypes: [],
  getCountrySetupData: async () => {
    set({ loading: true });
    try {
      const response = await axiosInstance.get(
        '/backoffice/payout-country-setup',
      );

      if (response?.data?.value?.isSuccess) {
        const {
          localCurrencyCode,
          supportedCurrencies,
          identityDocumentTypes,
          businessDocumentType,
        } = response.data.value.value;
        const { activeCountries: countries } = useRateStore.getState();

        const exchangeCurrencies = [
          { currency: localCurrencyCode, type: 'from' },
          ...supportedCurrencies.map((currency: string) => ({
            currency,
            type: 'to',
          })),
        ];

        const updatedTransferCurrencies = exchangeCurrencies.map((item) => {
          const matchingCountry = countries.find(
            (country) => country.name === countryMap[item.currency],
          );

          return { ...item };
        });

        set({
          loading: false,
          fromCurrencies: updatedTransferCurrencies.filter(
            ({ type }) => type === 'from',
          ),
          toCurrencies: updatedTransferCurrencies.filter(
            ({ type }) => type === 'to',
          ),
          identityDocumentTypes: identityDocumentTypes,
        });

        useOnboardingStore.getState().setBusinessDocuments(
          businessDocumentType.map((docType: string) => {
            return {
              docLabel: toTitleCaseFromUnderscore(docType),
              docValue: docType,
              docId: null,
            };
          }),
        );
      }

      return response.data.value;
    } catch (err: unknown) {
      errorHandler(err);
      set({ loading: false });
      return null;
    }
  },
}));

export default useResourceStore;
